import { ReactNode, useEffect, useRef, useState } from "react";

export interface DropdownOption {
  label: string | React.ReactNode;
  value: string | number;
  disabled?: boolean;
  disabledMessage?: string;
  className?: string;
}

export type DropdownPosition =
  | "bottom-left"
  | "bottom"
  | "bottom-right"
  | "top-left"
  | "top"
  | "top-right";

export interface DropdownProps {
  options: DropdownOption[];
  anchor?: ReactNode;
  onChange?: (selectedValue: string | number) => any;
  disabled?: boolean;
  className?: string;
  listClassName?: string;
  position?: DropdownPosition;
  autoFit?: boolean;
  autoFitBottomOffset?: number;
}

export default function Dropdown({
  options,
  anchor = "Select an option",
  onChange,
  disabled = false,
  className = "",
  position = "bottom-left",
  autoFit = true,
  listClassName = "",
  autoFitBottomOffset = 0
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [finalPosition, setFinalPosition] = useState<DropdownPosition>(position);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current && listRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const listHeight = listRef.current.offsetHeight;
      const spaceBelow =
        window.innerHeight - buttonRect.bottom - 25 * options.length - autoFitBottomOffset;
      const spaceAbove = buttonRect.top;

      if (autoFit) {
        console.debug("spacings are as follows", {
          spaceBelow,
          listHeight,
          spaceAbove
        });
        if (spaceBelow < listHeight && spaceAbove > spaceBelow) {
          setFinalPosition(position.replace("bottom", "top") as DropdownPosition);
        } else {
          setFinalPosition(position.replace("top", "bottom") as DropdownPosition);
        }
      } else {
        setFinalPosition(position);
      }
    }
  }, [isOpen, autoFit, position, options.length, autoFitBottomOffset]);

  const handleSelect = (option: DropdownOption) => {
    if (onChange && !option.disabled) {
      onChange(option.value);
    }
    setIsOpen(false);
  };

  const getDropdownPosition = () => {
    switch (finalPosition) {
      case "bottom-left":
        return "top-full left-0 mt-1";
      case "bottom":
        return "top-full left-1/2 transform -translate-x-1/2 mt-1";
      case "bottom-right":
        return "top-full right-0 mt-1";
      case "top-left":
        return "bottom-full left-0 mb-1";
      case "top":
        return "bottom-full left-1/2 transform -translate-x-1/2 mb-1";
      case "top-right":
        return "bottom-full right-0 mb-1";
      default:
        return "top-full left-0 mt-1";
    }
  };

  return (
    <div className={`inline-block ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`bg-white text-left flex items-center w-full ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onClick={() => !disabled && setIsOpen(prev => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {anchor}
      </button>
      <div className="relative">
        {isOpen && (
          <ul
            ref={listRef}
            className={`absolute z-10 w-fit bg-white border rounded-md shadow-lg ${getDropdownPosition()} ${listClassName}`}
            role="listbox"
          >
            {options.length > 0 ? (
              options.map(option => (
                <li
                  key={option.value}
                  className={`${
                    typeof option.label === "string" ? "px-4 py-2" : ""
                  } flex items-center w-full whitespace-nowrap ${
                    option.disabled && typeof option.label === "string"
                      ? "text-gray-500 bg-gray-100 cursor-not-allowed"
                      : "hover:bg-gray-200 cursor-pointer"
                  } ${option.className || ""}`}
                  onClick={() => !option.disabled && handleSelect(option)}
                >
                  {option.disabled ? (
                    <span title={option.disabledMessage}>{option.label}</span>
                  ) : (
                    option.label
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No options available</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
